import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { ReaderStatus, SessionResult } from '../types';
import { calculateORP, calculateWordDelay, ORPResult } from '../utils/rsvp';
import { ContentSection, BufferConfig, DEFAULT_BUFFER_CONFIG } from '../types/content';
import useContentBuffer from './useContentBuffer';

export interface RampConfig {
  enabled: boolean;
  startWpm: number;
  endWpm: number;
  durationSeconds: number;
}

export interface UseRSVPReaderOptions {
  content?: string;
  sections?: ContentSection[]; // New: chunked sections for buffered reading
  initialWpm: number;
  onComplete: (result: SessionResult) => void;
  onWpmChange: (wpm: number) => void;
  rampConfig: RampConfig;
  progressMode?: 'word' | 'time';
  autoStart?: boolean;
  allowSectionNavigation?: boolean;
  allowTimeSkip?: boolean;
  enableKeyboardShortcuts?: boolean;
  bufferConfig?: BufferConfig; // New: buffer configuration
  bookId?: string;        // Optional book identifier for progress tracking
  chapterId?: string;     // Optional initial chapter identifier
  initialWordIndex?: number; // starting word index for resuming
  format?: string;        // Optional file format (txt, epub, pdf, etc.)
}

export interface UseRSVPReaderReturn {
  // State
  words: string[];
  currentIndex: number;
  status: ReaderStatus;
  wpm: number;
  displayWord: ORPResult | null;
  sessionMaxWpm: number;
  
  // Derived values
  progress: number;
  showNavArrows: boolean;
  totalWords: number;
  
  // Section info (available when using sections)
  currentSectionIndex?: number;
  nextSectionAvailable?: boolean;
  
  // Actions
  togglePlay: () => void;
  reset: () => void;
  finishSession: () => void;
  jumpToSection: (direction: 'prev' | 'next') => void;
  skipTime: (seconds: number) => void;
  handleWpmChange: (newWpm: number) => void;
  adjustWpm: (delta: number) => void;
  setCurrentIndex: (index: number) => void;
}

export function useRSVPReader({
  content,
  sections, // New: optional sections for buffered reading
  initialWpm,
  onComplete,
  onWpmChange,
  rampConfig,
  progressMode = 'word',
  autoStart = false,
  allowSectionNavigation = true,
  allowTimeSkip = false,
  enableKeyboardShortcuts = true,
  bufferConfig = DEFAULT_BUFFER_CONFIG,
  bookId,
  chapterId,
  initialWordIndex,
  format
}: UseRSVPReaderOptions): UseRSVPReaderReturn {
  // Determine if we're using section-based reading
  const useSections = !!(sections && sections.length > 0 && bufferConfig.enabled);

  // Buffer management for section-based reading
  // Note: initialSectionIndex and initialWordIndex will be set via jumpToSection when sections load
  const buffer = useContentBuffer({
    sections: sections || [],
    initialSectionIndex: 0,
    initialWordIndex: 0,
    bufferConfig,
  });

  // Destructure stable references from buffer
  const { jumpToSection: bufferJump, currentSectionIndex: bufferCurrentSectionIndex } = buffer;
  
  // State
  const [words, setWords] = useState<string[]>([]);
  const [sectionIndices, setSectionIndices] = useState<number[]>([]);
  const [currentIndex, setCurrentIndex] = useState(() => {
    // If an initial word index is provided and valid, use it; otherwise start at 0
    const idx = typeof initialWordIndex === 'number' && initialWordIndex > 0 ? initialWordIndex : 0;
    return idx;
  });
  const [status, setStatus] = useState<ReaderStatus>(ReaderStatus.IDLE);
  const [wpm, setWpm] = useState(rampConfig.enabled ? rampConfig.startWpm : initialWpm);
  const [displayWord, setDisplayWord] = useState<ORPResult | null>(null);
  const [sessionMaxWpm, setSessionMaxWpm] = useState(initialWpm);

  // Refs
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const elapsedRef = useRef<number>(0);
  const sessionStartTimeRef = useRef<number | null>(null);
  const sessionStartIndexRef = useRef<number>(0); // Track starting word index for session (offset within section)
  const bookIdRef = useRef<string>(bookId || '');
  const chapterIdRef = useRef<string>(chapterId || '');
  const initialWordIndexRef = useRef<number>(typeof initialWordIndex === 'number' && initialWordIndex > 0 ? initialWordIndex : 0);
  const hasCompletedRef = useRef<boolean>(false);
  const wasPausedRef = useRef<boolean>(false); // Track if session was ever paused

  // Refs to hold latest values without causing callback re-renders
  const currentIndexRef = useRef(currentIndex);
  useEffect(() => { currentIndexRef.current = currentIndex; }, [currentIndex]);

  const wpmRef = useRef(wpm);
  useEffect(() => { wpmRef.current = wpm; }, [wpm]);

  const sessionMaxWpmRef = useRef(sessionMaxWpm);
  useEffect(() => { sessionMaxWpmRef.current = sessionMaxWpm; }, [sessionMaxWpm]);

  // Update refs when bookId/chapterId change
  useEffect(() => {
    bookIdRef.current = bookId || '';
  }, [bookId]);

  useEffect(() => {
    chapterIdRef.current = chapterId || '';
  }, [chapterId]);

  // Keep initialWordIndexRef in sync if prop changes (should be stable per session)
  useEffect(() => {
    const val = typeof initialWordIndex === 'number' && initialWordIndex > 0 ? initialWordIndex : 0;
    initialWordIndexRef.current = val;
  }, [initialWordIndex]);

  // Parse content into words and sections
  useEffect(() => {
    // Reset completion guard when content changes
    hasCompletedRef.current = false;

    // If using sections, extract words from the appropriate section based on initialWordIndex
    if (useSections && sections && sections.length > 0) {
      const targetGlobalIndex = initialWordIndexRef.current;
      let targetSectionIdx = -1;
      let wordOffset = 0;

      // Try to find the section that contains the target global word index
      if (targetGlobalIndex > 0) {
        const foundIdx = sections.findIndex(s => {
          if (!s.content) return false;
          return targetGlobalIndex >= s.startWordIndex && targetGlobalIndex <= s.endWordIndex;
        });
        if (foundIdx !== -1) {
          targetSectionIdx = foundIdx;
          wordOffset = targetGlobalIndex - sections[targetSectionIdx].startWordIndex;
        }
      }

      // If not found, fall back to the first section with content
      if (targetSectionIdx === -1) {
        targetSectionIdx = sections.findIndex(s => s.content);
        wordOffset = 0;
      }

      const sectionCandidate = sections[targetSectionIdx];
      if (targetSectionIdx >= 0 && sectionCandidate?.content) {
        const wordsArray = sectionCandidate.content.split(/\s+/).filter(w => w.length > 0);
        setWords(wordsArray);
        setSectionIndices([targetSectionIdx]);
        setCurrentIndex(wordOffset);
        // Sync buffer to the target section
        if (bufferJump) {
          bufferJump(targetSectionIdx);
        }
        // Initialize session state for autoStart or idle
        if (autoStart) {
          setStatus(ReaderStatus.PLAYING);
          sessionStartTimeRef.current = Date.now();
          // Session starts at the offset within the section
          sessionStartIndexRef.current = wordOffset;
        } else {
          setStatus(ReaderStatus.IDLE);
        }
        return;
      }
      // If no valid section found, fall through to legacy handling
    }

    // Legacy: parse full content string (skip if no content)
    if (!content) return;

    const rawSections = content.split(/\n\s*\n/);
    const newWords: string[] = [];
    const newSectionIndices: number[] = [];

    rawSections.forEach(section => {
      const sectionWords = section.split(/\s+/).filter(w => w.length > 0);
      if (sectionWords.length > 0) {
        newSectionIndices.push(newWords.length);
        newWords.push(...sectionWords);
      }
    });

    setWords(newWords);
    setSectionIndices(newSectionIndices);
    // Use initialWordIndex if provided and valid
    const startIdx = (initialWordIndexRef.current && initialWordIndexRef.current < newWords.length)
      ? initialWordIndexRef.current
      : 0;
    setCurrentIndex(startIdx);
    elapsedRef.current = 0;

    if (autoStart) {
      setStatus(ReaderStatus.PLAYING);
      sessionStartTimeRef.current = Date.now();
      // Session starts at the initial word index (or 0)
      sessionStartIndexRef.current = startIdx;
    } else {
      setStatus(ReaderStatus.IDLE);
    }

    const startSpeed = rampConfig.enabled ? rampConfig.startWpm : initialWpm;
    setWpm(startSpeed);
    setSessionMaxWpm(startSpeed);
  }, [content, sections, useSections, rampConfig.enabled, rampConfig.startWpm, initialWpm, autoStart, bookId, chapterId, bufferJump]);

  // Update WPM if config changes while idle
  useEffect(() => {
    if (status === ReaderStatus.IDLE) {
      const startSpeed = rampConfig.enabled ? rampConfig.startWpm : initialWpm;
      setWpm(startSpeed);
      elapsedRef.current = 0;
    }
  }, [rampConfig, initialWpm, status]);

  // Track Max WPM
  useEffect(() => {
    if (status === ReaderStatus.PLAYING && wpm > sessionMaxWpm) {
      setSessionMaxWpm(wpm);
    }
  }, [wpm, status, sessionMaxWpm]);

  // Update display word ORP when index changes
  useEffect(() => {
    if (words.length > 0 && currentIndex < words.length) {
      setDisplayWord(calculateORP(words[currentIndex]));
    }
  }, [currentIndex, words]);

  // Notify buffer of current word index for section transitions
  useEffect(() => {
    if (useSections && buffer.setCurrentWordIndex) {
      buffer.setCurrentWordIndex(currentIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, useSections, buffer.setCurrentWordIndex]);

  // Calculate stats and trigger completion (stable, uses refs for volatile values)
  const finishSession = useCallback(() => {
    if (hasCompletedRef.current) return;
    hasCompletedRef.current = true;

    setStatus(ReaderStatus.COMPLETED);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    const durationSeconds = elapsedRef.current / 1000;
    const durationMinutes = durationSeconds / 60;

    const calculatedAvgWpm = durationMinutes > 0
      ? Math.round(currentIndexRef.current / durationMinutes)
      : wpmRef.current;

    // Determine current chapterId and global start word index
    let effectiveChapterId = chapterIdRef.current;
    let globalStartIndex = sessionStartIndexRef.current; // default for legacy

    if (useSections && sections && bufferCurrentSectionIndex !== undefined) {
      const currentSection = sections[bufferCurrentSectionIndex];
      if (currentSection?.id) {
        effectiveChapterId = currentSection.id;
      }
      // Compute global start: section's startWordIndex + offset within section at session start
      globalStartIndex = (currentSection?.startWordIndex || 0) + sessionStartIndexRef.current;
    }

    const wordsReadInSession = currentIndexRef.current - sessionStartIndexRef.current;

    const result: SessionResult = {
      wordsRead: wordsReadInSession,
      durationSeconds: durationSeconds,
      maxWpm: sessionMaxWpmRef.current,
      avgWpm: calculatedAvgWpm,
      date: new Date().toISOString(),
      bookId: bookIdRef.current || undefined,
      chapterId: effectiveChapterId || undefined,
      startWordIndex: globalStartIndex,
      accuracy: 100, // Default: perfect accuracy (no comprehension test)
      paused: wasPausedRef.current,
      format: format || undefined,
    };

    onComplete(result);
  }, [onComplete, useSections, sections, bufferCurrentSectionIndex]);

  // Main reading loop step
  const step = useCallback(() => {
    setCurrentIndex((prev: number) => {
      const next = prev + 1;
      if (next >= words.length) {
        finishSession();
        return prev;
      }
      return next;
    });
  }, [words.length, finishSession]);

  // Timer effect
  useEffect(() => {
    if (status === ReaderStatus.PLAYING) {
      if (!sessionStartTimeRef.current) {
        sessionStartTimeRef.current = Date.now();
      }

      if (progressMode === 'time' && rampConfig.enabled) {
        if (elapsedRef.current >= rampConfig.durationSeconds * 1000) {
          finishSession();
          return;
        }
      }

      const currentWord = words[currentIndex];
      const delay = calculateWordDelay(wpm, currentWord);

      timeoutRef.current = setTimeout(() => {
        if (rampConfig.enabled) {
          elapsedRef.current += delay;
          const elapsedSec = elapsedRef.current / 1000;

          if (elapsedSec < rampConfig.durationSeconds) {
            const progress = elapsedSec / rampConfig.durationSeconds;
            const range = rampConfig.endWpm - rampConfig.startWpm;
            const newSpeed = Math.floor(rampConfig.startWpm + (range * progress));
            setWpm(newSpeed);
          } else {
            setWpm(rampConfig.endWpm);
          }
        } else {
          elapsedRef.current += delay;
        }

        step();
      }, delay);
    }

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [status, wpm, currentIndex, words, step, rampConfig, progressMode, finishSession]);

  // Actions
  const togglePlay = useCallback(() => {
    setStatus((prev: ReaderStatus) => {
      if (prev === ReaderStatus.PLAYING) {
        // Pausing - mark that session was paused
        wasPausedRef.current = true;
        return ReaderStatus.PAUSED;
      }
      // Starting to play - record session start index if not already set
      if (prev === ReaderStatus.IDLE || prev === ReaderStatus.PAUSED) {
        if (sessionStartTimeRef.current === null) {
          sessionStartTimeRef.current = Date.now();
          // Use the current index from ref (which may be from initialWordIndex) as session start
          sessionStartIndexRef.current = currentIndexRef.current;
        }
      }
      return ReaderStatus.PLAYING;
    });
  }, []); // No dependencies needed; uses refs and setStatus (stable)

  const reset = useCallback(() => {
    hasCompletedRef.current = false;
    setStatus(ReaderStatus.IDLE);
    setCurrentIndex(0);
    elapsedRef.current = 0;
    sessionStartTimeRef.current = null;
    sessionStartIndexRef.current = 0;
    const startSpeed = rampConfig.enabled ? rampConfig.startWpm : initialWpm;
    setWpm(startSpeed);
    setSessionMaxWpm(startSpeed);
  }, [rampConfig.enabled, rampConfig.startWpm, initialWpm]);

  const handleWpmChange = useCallback((newWpm: number) => {
    if (rampConfig.enabled) return;
    setWpm(newWpm);
    onWpmChange(newWpm);
  }, [rampConfig.enabled, onWpmChange]);

  const jumpToSection = useCallback((direction: 'prev' | 'next') => {
    // If using sections, use buffer navigation and update words
    if (useSections && sections) {
      const currentIdx = bufferCurrentSectionIndex;
      let targetIdx = currentIdx;
      if (direction === 'next') targetIdx++;
      else targetIdx--;
      if (targetIdx < 0 || targetIdx >= sections.length) return;
      // Update buffer
      bufferJump(targetIdx);
      // Update words to the target section
      const targetSection = sections[targetIdx];
      if (targetSection?.content) {
        const newWords = targetSection.content.split(/\s+/).filter(w => w.length > 0);
        setWords(newWords);
        setCurrentIndex(0);
      }
      return;
    }

    // Legacy: array-based navigation
    if (!allowSectionNavigation || sectionIndices.length < 2) return;

    if (direction === 'next') {
      const nextIndex = sectionIndices.find((idx: number) => idx > currentIndex);
      if (nextIndex !== undefined) setCurrentIndex(nextIndex);
      else setCurrentIndex(words.length - 1);
    } else {
      const prevIndex = [...sectionIndices].reverse().find((idx: number) => idx < currentIndex);
      if (prevIndex !== undefined) setCurrentIndex(prevIndex);
      else setCurrentIndex(0);
    }
  }, [useSections, sections, bufferCurrentSectionIndex, bufferJump, allowSectionNavigation, sectionIndices, currentIndex, words.length]);

  const skipTime = useCallback((seconds: number) => {
    if (!allowTimeSkip) return;
    const wordsToSkip = Math.floor((wpm / 60) * seconds);
    setCurrentIndex((prev: number) => {
      const next = prev + wordsToSkip;
      if (next < 0) return 0;
      if (next >= words.length) return words.length - 1;
      return next;
    });
  }, [allowTimeSkip, wpm, words.length]);

  const adjustWpm = useCallback((delta: number) => {
    if (rampConfig.enabled) return; // Can't adjust during ramp
    const newWpm = Math.max(50, Math.min(10000, wpm + delta));
    setWpm(newWpm);
    onWpmChange(newWpm);
  }, [rampConfig.enabled, wpm, onWpmChange]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!enableKeyboardShortcuts) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Spacebar to toggle play/pause
      if (e.code === 'Space') {
        e.preventDefault();
        togglePlay();
      }
      // Left arrow to decrease speed
      else if (e.code === 'ArrowLeft') {
        e.preventDefault();
        adjustWpm(-25);
      }
      // Right arrow to increase speed
      else if (e.code === 'ArrowRight') {
        e.preventDefault();
        adjustWpm(25);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enableKeyboardShortcuts, togglePlay, adjustWpm]);

  // Derived values
  const progress = useMemo(() => {
    if (progressMode === 'time' && rampConfig.enabled) {
      return Math.min((elapsedRef.current / (rampConfig.durationSeconds * 1000)) * 100, 100);
    }
    return words.length > 0 ? (currentIndex / words.length) * 100 : 0;
  }, [progressMode, rampConfig.enabled, rampConfig.durationSeconds, currentIndex, words.length]);

  const showNavArrows = useMemo(() => {
    // If using sections, show arrows based on buffer state
    if (useSections && sections) {
      return bufferConfig.enabled && sections.length > 1;
    }
    return allowSectionNavigation && sectionIndices.length > 1;
  }, [allowSectionNavigation, sectionIndices.length, useSections, sections, bufferConfig.enabled]);

  const totalWords = words.length;

  return {
    // State
    words,
    currentIndex,
    status,
    wpm,
    displayWord,
    sessionMaxWpm,
    
    // Derived values
    progress,
    showNavArrows,
    totalWords,
    
    // Buffer state (when using sections)
    currentSectionIndex: useSections ? buffer.currentSectionIndex : undefined,
    nextSectionAvailable: useSections ? !!buffer.nextSection : undefined,
    
    // Actions
    togglePlay,
    reset,
    finishSession,
    jumpToSection,
    skipTime,
    handleWpmChange,
    adjustWpm,
    setCurrentIndex
  };
}

export default useRSVPReader;
